import { useParams } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import API from '../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import CustomerInfo from '../smallComponent/CustomerInfo';
import { API_IMAGE_URL } from '../../lib/api';
import TotalSummary from '../payment/TotalSummary';
import CustomerFilterBar from '../smallComponent/CustomerFilterBar';
import * as XLSX from 'xlsx';

const EmployeeDetailView = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewMoreWorkDetails, setViewMoreWorkDetails] = useState([]);

  const pageSize = 30;

  // ✅ Fetch employee details on load
  useEffect(() => {
    fetchEmployee();
    // eslint-disable-next-line
  }, []);

  // ✅ Reapply filter whenever customers or filter inputs change
  useEffect(() => {
    handleFilter();
  }, [search, startDate, endDate, customers]);

  // ✅ Fetch employee info
  const fetchEmployee = async () => {
    try {
      const res = await API.get(`/employees/${id}`);
      const emp = res.data.employee;
      setEmployee(emp);

      if (emp?.id && emp?.department) {
        fetchCustomers(emp.id, emp.department);
      }
    } catch (error) {
      console.error('❌ Failed to fetch employee:', error);
    }
  };

  // ✅ Fetch customers for this employee
  const fetchCustomers = async (employee_id, department) => {
    try {
      const res = await API.get('/customers/admin', { params: { employee_id, department } });
      const allCustomers = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];

      const doneCustomers = allCustomers.filter(c => Number(c.status) === 2);

      const cleaned = doneCustomers.map(c => {
        let details = [];
        if (Array.isArray(c.work_process_details)) {
          details = c.work_process_details;
        } else if (typeof c.work_process_details === 'string') {
          try {
            details = JSON.parse(c.work_process_details || '[]');
            if (!Array.isArray(details)) details = [];
          } catch {
            details = [];
          }
        }
        return { ...c, work_process_details: details };
      });

      setCustomers(cleaned);
    } catch (err) {
      console.error('❌ Fetch error:', err);
    }
  };

  // ✅ Filtering logic
  const handleFilter = () => {
    if (!Array.isArray(customers)) return;

    const filtered = customers.filter(c => {
      const nameMatch =
        !search ||
        c.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.contact_number?.includes(search) ||
        c.business_name?.toLowerCase().includes(search.toLowerCase());

      const doneDate = c.done_date ? new Date(c.done_date) : null;
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);

      const dateMatch =
        (!start || (doneDate && doneDate >= start)) &&
        (!end || (doneDate && doneDate <= end));

      return nameMatch && dateMatch;
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  };

  // ✅ Reset filter
  const resetFilter = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setFilteredCustomers(customers);
    setCurrentPage(1);
  };

  // ✅ Export filtered customers to Excel
  const exportToExcel = () => {
    const data = filteredCustomers.map((c, i) => ({
      SN: i + 1,
      Name: c.customer_name,
      Phone: c.contact_number,
      Business: c.business_name,
      StartDate: c.project_start_date,
      DoneDate: c.done_date,
      Developer: c.developer_name,
      Salesman: c.salesman_name,
      Price: c.price,
      Due: c.due_amount
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.writeFile(workbook, 'CustomerList.xlsx');
  };

  // ✅ Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / pageSize);
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentCustomers = showAll
    ? filteredCustomers
    : filteredCustomers.slice(indexOfFirst, indexOfLast);

  // ✅ Modal handling
  const openViewMoreModal = (workDetails) => {
    setViewMoreWorkDetails(workDetails || []);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setViewMoreWorkDetails([]);
  };

  if (!employee) return <div className="text-center py-6 text-gray-500">Loading employee details...</div>;

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Employee Info */}
      <div className="bg-white p-6 rounded-lg shadow-md border mb-6">
        <h2 className="text-2xl font-bold mb-2 text-gray-800">
          {employee.full_name} ({employee.employee_id})
        </h2>

        <TotalSummary customers={filteredCustomers} />
      </div>

      {/* Customers Table */}
      <div className="bg-white p-6 rounded-lg shadow-md border">
        <h3 className="text-xl font-semibold mb-4">✅ Completed Projects</h3>

        {/* Filter Bar */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <CustomerFilterBar
              search={search}
              setSearch={setSearch}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              onExport={exportToExcel}
              onReset={resetFilter}
            />
          </Card.Body>
        </Card>

        {filteredCustomers.length === 0 ? (
          <p className="text-gray-500">No completed customers found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300 rounded text-sm">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="px-3 py-2 border">SN</th>
                  <th className="px-3 py-2 border">Customer Info</th>
                  <th className="px-3 py-2 border">Developer(s)</th>
                  <th className="px-3 py-2 border">Start Date</th>
                  <th className="px-3 py-2 border">Work Details</th>
                  <th className="px-3 py-2 border">Salesman / Duration</th>
                  <th className="px-3 py-2 border">Price</th>
                  <th className="px-3 py-2 border">Work Progress</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map((c, i) => (
                  <tr key={c.id} className="even:bg-gray-50">
                    <td className="border px-3 py-2 text-center">{showAll ? i + 1 : indexOfFirst + i + 1}</td>
                    <td className="border px-3 py-2"><CustomerInfo customer={c} /></td>
                    <td className="border px-3 py-2">
                      <div className="text-purple-700 font-semibold">
                        <FontAwesomeIcon icon={faCheck} /> {c.developer_name}
                      </div>
                      {c.codeveloper_team?.length > 0 ? (
                        c.codeveloper_team.map(dev => (
                          <div key={dev.id}>
                            <FontAwesomeIcon icon={faCheck} /> {dev.full_name}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400">No Co-Developers</span>
                      )}
                    </td>
     <td className="border px-3 py-2">
          {c.done_date
            ? new Date(c.done_date).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '-'}
        </td>
                    <td className="border px-3 py-2">
                      {c.work_details}
                      {c.upload_quatation && (
                        <a
                          href={`${API_IMAGE_URL}quotations/${c.upload_quatation}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block text-blue-600 hover:underline"
                        >
                          📄 View Quotation
                        </a>
                      )}
                    </td>
                    <td className="border px-3 py-2">
                      👤 {c.salesman_name || <em className="text-gray-500">Not Assigned</em>}<br />
                      ⏱️ {c.project_duration || 'N/A'} Days
                    </td>
                    <td className="border px-3 py-2 text-center">
                      <span className="text-green-600 font-bold">₹{c.price}</span><br />
                      <small className="text-red-600">Due: ₹{c.due_amount}</small>
                    </td>
                    <td className="border px-3 py-2">
                      {c.work_process_details.length > 0 ? (
                        <ul className="list-disc pl-5 max-h-20 overflow-y-auto text-gray-700">
                          {c.work_process_details.slice(-2).map((w, wi) => (
                            <li key={wi}><strong>{w.date || 'N/A'}:</strong> {w.text || w}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-400">No updates yet</span>
                      )}

                      {c.work_process_details.length > 2 && (
                        <button
                          onClick={() => openViewMoreModal(c.work_process_details)}
                          className="mt-2 px-3 py-1 border border-blue-600 text-blue-600 rounded text-xs hover:bg-blue-50"
                        >
                          View More
                        </button>
                      )}

                      <div className="relative w-full h-5 mt-2 bg-gray-300 rounded border">
                        <div
                          className={`h-full text-center text-white text-xs rounded ${
                            c.work_percentage >= 80
                              ? 'bg-green-600'
                              : c.work_percentage >= 50
                              ? 'bg-yellow-400'
                              : 'bg-red-600'
                          }`}
                          style={{ width: `${c.work_percentage}%` }}
                        >
                          {c.work_percentage}%
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination + Show All */}
      <div className="mt-3 flex justify-end gap-2">
        {!showAll && (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              ◀ Prev
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next ▶
            </Button>
          </>
        )}
        <Button
          size="sm"
          variant={showAll ? 'warning' : 'info'}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? '🔁 Paginate' : '📄 Show All'}
        </Button>
      </div>

      {/* Modal for Work Process Details */}
      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-6 relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-2 right-3 text-gray-600 hover:text-gray-900 text-2xl font-bold"
              aria-label="Close modal"
            >
              &times;
            </button>
            <h3 className="text-xl font-semibold mb-4">All Work Process Details</h3>
            {viewMoreWorkDetails.length > 0 ? (
              <ul className="list-disc pl-6 space-y-1">
                {viewMoreWorkDetails.map((w, i) => (
                  <li key={i}>
                    <strong>{w.date || 'N/A'}:</strong> {w.text || w}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No work process details available.</p>
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default EmployeeDetailView;
